import { EAgoraErrorCode } from '../types/agora';
import { EKey } from '../types/general';
import { EAudioMixingMode } from '../types/media';
import { EUserEntitlement, EUserPreference } from '../types/user';
import { getLiveConfigValue } from '../utils/general/configuration';
import { environmentConfig } from './EnvironmentConfig';
import { localStorageConfig } from './LocalStorageConfig';

export const liveApplicationConfig = {
  /**
   * Confirm modal, which appears, when a user add all templates.
   * todo: UseRef / component-level global.
   */
  ASK_TO_REPLACE_ALL_SCENES: true,
  /**
   * Configuration for audio tracks management.
   */
  AUDIO_TRACKS: {
    DEFAULT_LANGUAGES_COUNT: 2,
    DEFAULT_LANGUAGE: 'en',
  },
  /**
   * Whether application is currently in active state.
   */
  IS_ACTIVE: true,
  TIMING: {
    /**
     * Timestamp indicating when page config was initialized and app is starting.
     */
    STARTED_AT: Date.now(),
    /**
     * Time duration of supposed idle state.
     */
    IDLE_TIME: 0,
    /**
     * Time check interval for idle state.
     * Once per 10 sec increment and check it.
     */
    IDLE_TIME_CHECK_INTERVAL: 10_000,
    /**
     * Idle state timeout.
     * Time that is considers page as inactive after some period of time.
     * 15 min as base version, should be adjusted to match desired logic later.
     */
    IDLE_TIME_TIMEOUT: 15 * 60_000,
    /**
     * Number in MS indicating clock skew of local machine comparing to google servers.
     * Maintained with subscription to FB server
     */
    CLOCK_SKEW: 0,
    /**
     * Timestamp indicating when last time of JWT refresh / change happened.
     */
    JWT_UPDATED_AT: 0,
    /**
     * Whether timing intervals debug enabled.
     * Enables profiling of page intervals and checking of intervals execution duration.
     */
    DEBUG_TIMERS: getLiveConfigValue(localStorageConfig.DEBUG.INTERVALS_DEBUG_ENABLED, false),
    /**
     * Delay for schedule studio start timer
     */
    MAXIMAL_EXPECTED_INGEST_START_DELAY: -30000,
  },
  // todo: Rename?
  EVENT: {
    STREAM_STATS_POLLING_INTERVAL: 8000,
    MAXIMAL_EXPECTED_INGEST_START_DELAY: 30000,
    VIDEO_TRANSCODING_POLLING_INTERVAL: 3000,
    STATUS_POLLING_INTERVAL: 5000,
    STATUS_ERRORS_NOTIFICATION_THRESHOLD: 10,
    DURATION_RECALCULATION_INTERVAL: 500,
  },
  GUESTS: {
    /**
     * Keep in sync with backend validation.
     */
    MAX_GUEST_MAIL_LENGTH: 128,
    MAX_GUEST_NAME_LENGTH: 64,
    MAX_GUEST_TITLE_LENGTH: 64,
    LIMIT: 12,
  },
  /**
   * Whether whole store observer should be combined or isolated providers.
   * If combined, we try to push change to all react providers at once event if they are not changed.
   * If not, every small provider is responsible only for one context manager.
   */
  USE_COMBINED_PROVIDERS: getLiveConfigValue(localStorageConfig.LIVE.COMBINED_PROVIDERS, false),
  TRACKING: {
    /* Probably restrict dev env tracking (vimeo.dev, not ci) to prevent sending logs on HMR or debugging  */
    IS_ENABLED: true,
    IS_BP_LOG_ENABLED: getLiveConfigValue(localStorageConfig.DEBUG.BP_TRACKING_LOGGING, false),
    IS_AGORA_STATS_COLLECTING_ENABLED: getLiveConfigValue(
      localStorageConfig.DEBUG.AGORA_STATS_COLLECTING,
      false,
    ),
    IS_DATADOG_LOG_ENABLED: getLiveConfigValue(
      localStorageConfig.DEBUG.DATADOG_TRACKING_LOGGING,
      environmentConfig.IS_LOCAL_DEV,
    ),
    IS_EMBED_MESSAGING_LOG_ENABLED: getLiveConfigValue(
      localStorageConfig.DEBUG.EMBED_MESSAGING_ACTIONS_LOGGING,
      true,
    ),
    /**
     * Whether firebase subscriptions/actions should be tracked.
     */
    IS_FIREBASE_LOG_ENABLED: getLiveConfigValue(localStorageConfig.DEBUG.FB_ACTIONS_LOGGING, false),
    IS_FIREBASE_SDK_LOG_ENABLED: getLiveConfigValue(localStorageConfig.DEBUG.FB_SDK_LOGGING, false),
    IS_INTERVAL_CHECKING_ENABLED: getLiveConfigValue(
      localStorageConfig.DEBUG.INTERVALS_DEBUG_ENABLED,
      false,
    ),
    /**
     * Limit errors tracked by event to prevent global level spamming and causing unusual spikes for broken events.
     * Or when something goes totally wrong and unusual count of reports is sent.
     */
    NR_LOG_ERRORS_TRACKED: 0,
    NR_LOG_ERRORS_LIMIT: 200,
    /**
     * Error codes that should not appear in NR generally.
     * Intended to use with error.code properties where possible.
     */
    NR_AGORA_IGNORED_CODES: [EAgoraErrorCode.PERMISSION_DENIED, EAgoraErrorCode.OPERATION_ABORTED],
    /**
     * Ignore list of test/specific users.
     *  - Currently ignores users that are used for health checks
     */
    NR_IGNORED_USERS: [142303705, 142305643, 142310438, 142312049, 142312785, 142313699],
    BP_IGNORED_USERS: [142303705, 142305643, 142310438, 142312049, 142312785, 142313699],
    BP_FREQUENT_EVENTS_THROTTLE_PERIOD: 5000,
  },
  MARKETING: {
    QNA_UPSELL_LAST_EVENT_ID: getLiveConfigValue(localStorageConfig.MARKETING.QNA_UPSELL, 0),
    STREAMING_4K_UPSELL_TIMEOUT: 7 * 24 * 60 * 60 * 1000, // 7 days
    STREAMING_4K_UPSELL_LAST_EVENT_ID: getLiveConfigValue(
      localStorageConfig.MARKETING.STREAMING_4K_UPSELL,
      0,
    ),
  },
  MEDIA: {
    // For cases when switching scene and do not want to see previous frames.
    MEDIA_TRANSITION_TIMEOUT: 250,
    MIN_VIDEO_SEEK_CHECK_INTERVAL: 50,
    MAX_VIDEO_SEEK_CHECK_INTERVAL: 500,
    VIDEO_TIME_LEFT_CHECK_INTERVAL: 500,
    SPEAKING_NOTIFICATION_INTERVAL: 5000,
    DEFAULT_AUDIO_MIXING_MODE: EAudioMixingMode.ALL,
  },
  USER: {
    /**
     * Capabilities of owner to be fetched.
     * Can be extended to get feature flags or restrictions on initial load.
     */
    REQUIRED_OWNER_CAPABILITIES: [
      'canSendGuestEmailInvites',
      'canUseBackupStream',
      'hasAutoClosedCaptions',
      'hasBrandkitsEnabled',
      'hasDashStreaming',
      'hasDisableAutoArchiving',
      'hasEcdn',
      'hasEnterprise',
      'hasGeoBlock',
      'hasLiveComposerApp',
      'hasLiveSubscription',
      'hasLiveSupportChat',
      'hasScheduledMediaPlayback',
      'hasUnlimitedLiveEventDuration',
      'hasUhdStreaming',
    ],
    /**
     * Entitlements of owner to be fetched.
     */
    REQUIRED_OWNER_ENTITLEMENTS: [
      EUserEntitlement.LIVE_COMPOSER_TEMPLATES_ACCESS,
      EUserEntitlement.LIVE_GUEST_API_MANAGEMENT,
      EUserEntitlement.LIVE_MEDIA_API_MANAGEMENT,
    ],
    /**
     *  Preferences of owner to be fetched.
     *  */
    REQUIRED_OWNER_PREFERENCES: [
      EUserPreference.EXTENDED_STREAM_WITH_DVR,
      EUserPreference.EXTENDED_STREAM_WITH_SIMULCAST,
      EUserPreference.AUDIO_TRACKS_MANAGEMENT,
    ],
    /**
     * Preferences required to show tips/tps.
     * Based on current user context.
     */
    REQUIRED_PREFERENCES: [
      EUserPreference.ONBOARDING_RTMP,
      EUserPreference.ONBOARDING_RTC,
      EUserPreference.ONBOARDING_RTC_BOKEH,
      EUserPreference.ONBOARDING_RTC_SIMULIVE,
      EUserPreference.PUBLISH_TO_SOCIAL,
    ],
  },
  FEATURE: {
    USE_RTC_PERFORMANCE_TOOLS: getLiveConfigValue(
      localStorageConfig.FEATURE.USE_RTC_PERFORMANCE_TOOLS,
      false,
    ),
    USE_TEST_VIDEOS_SEARCH: getLiveConfigValue(
      localStorageConfig.FEATURE.USE_TEST_VIDEOS_SEARCH,
      false,
    ),
    USE_MOBILE_ACCESS: getLiveConfigValue(localStorageConfig.FEATURE.USE_MOBILE_ACCESS, false),
    USE_MULTIPLE_AUDIO_TRACKS_IN_RTMP_PREVIEW: getLiveConfigValue(
      localStorageConfig.FEATURE.USE_MULTIPLE_AUDIO_TRACKS_IN_RTMP_PREVIEW,
      false,
    ),
  },
  KEY_BINDS: {
    KEYPRESS_THROTTLING_PERIOD: 100,
    ENABLED_KEYS: [EKey.ARROW_LEFT, EKey.ARROW_RIGHT],
  },
  AUTO_CC: {
    UPDATE_TIMEOUT: 5 * 600 * 1000, // 5 minutes
    MAX_KEYWORDS_COUNT: 50,
    DEFAULT_LANGUAGE: 'en-US',
    LANGUAGE_LIST: [
      { value: 'en-US', label: 'English' },
      { value: 'fr-FR', label: 'French' },
      { value: 'de-DE', label: 'German' },
      { value: 'es-ES', label: 'Spanish' },
      { value: 'pt-BR', label: 'Portuguese' },
      { value: 'zh-CN', label: 'Chinese' },
    ],
    // todo: Move links to vimeoConfig.
    UPGRADE_ENTERPRISE_LINK: '/enterprise/contact-us?mkc=auto_cc',
    UPGRADE_UNLIMITED_LINK: 'mailto:Accountmanagement@vimeo.com',
  },
  DOM: {
    LIVE_APP_NAMING_PREFIX: 'live',
    INTERACTION_APP_NAMING_PREFIX: 'interaction',
  },
};
